import { displayName } from "~/common/functions";
import { GDTF, type TimelineEntity } from "~/components/timeline";
import type { GetCompoundEntitiesForTimelineQuery } from "~/features/compoundEntities/gqls";
import type { Locale } from "~/i18n/resolveLocale";

type CompoundForTimeline = GetCompoundEntitiesForTimelineQuery["compoundCompoundRecords"][number];

const safeParseGDTF = (input: string, isStartDate: boolean): GDTF | null => {
  try {
    const gdtf = new GDTF(input, isStartDate);
    return gdtf.isValid() ? gdtf : null;
  } catch {
    return null;
  }
};

/**
 * Map a compound timeline record to a `TimelineEntity`. Categories come from the
 * pre-composed `categoryNameArray`. Ported from V1 `compoundRecordToTimelineEntity` (pure).
 */
export function toTimelineEntity(
  record: CompoundForTimeline,
  rowNumber: number,
  locale: Locale,
): TimelineEntity | null {
  if (!record.date) return null;

  const startGdtf = safeParseGDTF(record.date, true);
  if (!startGdtf) return null;

  let end: { year: number } | undefined;
  if (record.endDate) {
    const endGdtf = safeParseGDTF(record.endDate, false);
    if (endGdtf) end = { year: endGdtf.year };
  }

  return {
    id: record.id,
    title: displayName({ name: record.name, nameJa: record.nameJa }, locale),
    start: { year: startGdtf.year },
    end,
    categories: record.categoryNameArray,
    categoryNames: record.categoryNameArray,
    rowNumber,
  };
}
